'use client';

import { useState, useEffect, useMemo, useRef } from 'react';
import Link, { IntentLink, usePathname, useRouter } from '@/core/i18n/navigation';
import { fetchAPI } from '@/core/api/client';
import { Skeleton } from '@/core/components/ui/skeleton';
import { Button } from '@/core/components/ui/button';
import { Image as ImageIcon, ChevronLeft, ChevronRight, MoreHorizontal, ArrowRight } from 'lucide-react';
import { toast } from '@/core/utils/toast';
import { cn } from '@/core/utils/cn';
import { stripHtml } from '@/core/utils/strip-html';
import { cardOpensProductPage, getProductPrimaryAction, priceOnRequestLabel, servicePricingType, usesConfigurator } from '@/core/utils/product-action';
import Image from 'next/image';
import { getAbsoluteMediaUrl, getPublicMediaUrl, toOptimizableSrc } from '@/core/utils/media';
import { useCatalogContext } from './CatalogContext';
import { CatalogToolbar } from '@/modules/shop/components/catalog/catalog-toolbar';
import { appendFacetFilters, filtersKey } from '@/modules/shop/components/catalog/catalog-filter-utils';
import { useTranslation } from '@/core/components/providers/TranslationProvider';
import { useSiteConfig } from '@/core/components/providers/SiteConfigProvider';
import { trackEvent, trackAddToCart } from '@/core/utils/analytics';
import { ItemListTracker } from '@/core/components/Analytics/EcommerceTrackers';
import { formatPrice } from '@/core/utils/currency';
import { useSession } from 'next-auth/react';
import { LoginGateModal } from '@/modules/shop/components/negotiation/login-gate-modal';
import { NegotiatePriceModal } from '@/modules/shop/components/negotiation/negotiate-price-modal';
import { ProductActionIcon } from './shared/ProductActionIcon';
import { ProductBrandMark, type BrandDisplay } from './shared/ProductBrandMark';
import { useListingScrollRestore } from '@/core/hooks/useListingScrollRestore';
import { buildActionButtonStyle, buildActionButtonHoverCSS, ACTION_BUTTON_DEFAULTS } from './shared/actionButtonStyle';
import { isNegotiationAllowedForProduct } from '@/core/utils/negotiation';
import { WishlistHeart } from '@/modules/shop/components/wishlist/WishlistHeart';
import { appendBlockBrandFilter } from './shared/productBrandFilter';
import { resolveProductElementOrder, flattenProductElementOrder, type ProductElementOrderItem } from '@/core/utils/productCardOrder';
import { Carousel, carouselPropsFromSettings, type CarouselSettings } from './shared/Carousel';
import { useIsFirstProductGrid } from './FirstProductGridContext';
import { resolvePaginationAppearance, paginationItemStyle, paginationArrowStyle, paginationDotStyle, paginationContainerStyle, paginationHoverCss, paginationScopeClass } from './shared/paginationAppearance';

interface ProductGridProps extends CarouselSettings {
    /** The block's own id in the page's block tree — used to scope per-card hover CSS class
     *  names uniquely (combined with product.id, since one grid renders many cards). */
    blockId?: string;
    /** True only when this grid is the first block on the page (above the fold) — gates
     *  eager/priority image loading so grids further down the page don't compete with the
     *  real LCP image for bandwidth. See BlockRenderer's `isFirstBlock`. */
    isFirstBlock?: boolean;
    queryType?: 'latest' | 'category' | 'sale' | 'related' | 'current_category' | 'bundle' | 'manual';
    /** For 'related': what fills the block when the product has no (or too few) hand-picked related products. */
    relatedFallback?: 'none' | 'category' | 'latest' | 'bestseller' | 'sale';
    categoryId?: string;
    /** Brand slugs narrowing 'latest' / 'category' / 'sale' (e.g. "Nike" or "Next + Uniqlo" sections). */
    brandSlugs?: string[];
    /** Ordered product IDs for `queryType === 'manual'` — display order follows this array, not the API response. */
    manualProductIds?: number[];
    activeCategory?: any;
    columnsDesktop?: string;
    columnsMobile?: string;
    editorViewport?: 'desktop' | 'tablet' | 'mobile';
    showImage?: boolean;
    showTitle?: boolean;
    showPrice?: boolean;
    showBrand?: boolean;
    brandPosition?: 'above_price' | 'beside_price';
    brandLogoSize?: number;
    brandDisplay?: BrandDisplay;
    showButton?: boolean;
    showAddToCart?: boolean;
    showBuyNow?: boolean;
    showNegotiate?: boolean;
    buttonsOrder?: string[];
    buttonText?: string;
    addToCartText?: string;
    buyNowText?: string;
    negotiateText?: string;
    /** Shown instead of buttonText for a subscription plan / a service with subscribe-and-save
     *  tiers — the card links to the product page (no in-card tier/payment-mode picker). */
    viewOfferText?: string;
    isAdmin?: boolean;
    showDescription?: boolean;
    showRating?: boolean;
    showConditionBadge?: boolean;
    showSavingsBadge?: boolean;
    element_order?: string[];
    elementOrder?: string[];
    ratingColor?: string;
    imageFit?: 'cover' | 'contain';
    imageHoverEffect?: 'second_image' | 'zoom' | 'gallery_dots' | 'none';
    cardBorderRadius?: number;
    cardBorderWidth?: number;
    cardBorderColor?: string;
    cardHoverEffect?: boolean;
    titleColor?: string;
    titleFontSize?: number;
    priceColor?: string;
    priceFontSize?: number;
    /** @deprecated superseded by btnBgColor/btnTextColor (see ACTION_BUTTON_DEFAULTS) — still
     *  read as a fallback so pages saved before that change keep their configured look. */
    buttonBgColor?: string;
    buttonTextColor?: string;
    buttonBorderRadius?: number;
    // Action-button styling — same field-naming convention as ProductAddToCartBlock (product
    // page), so a single merchant-wide default can seed both blocks identically.
    iconType?: string;
    iconPosition?: 'left' | 'right';
    btnIconType?: string;
    btnIconPosition?: 'left' | 'right';
    btnBgColor?: string;
    btnTextColor?: string;
    btnBorderColor?: string;
    btnBorderWidth?: number;
    btnBorderRadius?: number;
    btnHoverBgColor?: string;
    btnHoverTextColor?: string;
    btnHoverBorderColor?: string;
    btnPaddingY?: number;
    btnPaddingX?: number;
    btnFontSize?: number;
    btnWidth?: number;
    buyNowIconType?: string;
    buyNowIconPosition?: 'left' | 'right';
    buyNowBgColor?: string;
    buyNowTextColor?: string;
    buyNowBorderColor?: string;
    buyNowBorderWidth?: number;
    buyNowBorderRadius?: number;
    buyNowHoverBgColor?: string;
    buyNowHoverTextColor?: string;
    buyNowHoverBorderColor?: string;
    buyNowPaddingY?: number;
    buyNowPaddingX?: number;
    buyNowFontSize?: number;
    buyNowWidth?: number;
    negotiateIconType?: string;
    negotiateIconPosition?: 'left' | 'right';
    negotiateBgColor?: string;
    negotiateTextColor?: string;
    negotiateBorderColor?: string;
    negotiateBorderWidth?: number;
    negotiateBorderRadius?: number;
    negotiateHoverBgColor?: string;
    negotiateHoverTextColor?: string;
    negotiateHoverBorderColor?: string;
    negotiatePaddingY?: number;
    negotiatePaddingX?: number;
    negotiateFontSize?: number;
    negotiateWidth?: number;
    savingsColor?: string;
    savingsNoBg?: boolean;
    savingsBgColor?: string;
    savingsFontSize?: number;
    savingsFontWeight?: string;
    savingsAlign?: 'left' | 'center' | 'right';
    savingsBorderRadius?: number;
    savingsBorderWidth?: number;
    savingsBorderColor?: string;
    savingsLabel?: string;
    imageQuality?: 'high' | 'compressed';
    limit?: number;
    /** 'carousel' lays the same cards out in one horizontally scrolling, snap-aligned row with
     *  prev/next arrows; columnsDesktop/columnsMobile then mean "cards visible at once". */
    layout?: 'grid' | 'carousel';
    /** Optional trailing "see all" tile in carousel mode (e.g. the category URL). */
    viewAllUrl?: string;
    viewAllText?: string;
    /** Key technical parameters strip on the card (element key 'specs'). */
    showSpecs?: boolean;
    specsCount?: number;
    /** Comma-separated spec names to show, in this order; empty = first `specsCount` rows. */
    specFields?: string;
    paginationEnabled?: boolean;
    paginationPosition?: 'left' | 'center' | 'right';
    paginationStyle?: 'numbers' | 'dots' | 'arrows';
    customStyles?: any;
    /** Server-prefetched products for this block's current query — skips the initial client fetch
     * (and the loading-skeleton flash) on first mount. See core/components/Builder/serverPrefetch.ts. */
    initialProducts?: any[];
}

function StarRating({ value, color = '#f59e0b' }: { value: number; color?: string }) {
    const full = Math.floor(value);
    const half = value - full >= 0.5;
    return (
        <span className="flex items-center gap-0.5" aria-label={`Ocena ${value.toFixed(1)}`}>
            {Array.from({ length: 5 }).map((_, i) => (
                <svg key={i} style={{ color: i < full || (i === full && half) ? color : undefined }} className={`h-3.5 w-3.5 ${i >= full && !(i === full && half) ? 'text-muted-foreground/30' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
            ))}
            <span className="text-[10px] ml-0.5 opacity-60">({value.toFixed(1)})</span>
        </span>
    );
}

// Picks the rows for the card's key-parameters strip. Section headers ("[Silnik]") and empty
// values never qualify. With an explicit name list only those rows show (a missing one is just
// skipped — padding with arbitrary other rows would put e.g. "Kolor" next to "Moc" on one card
// and "Masa" on the next); without one, the first `count` rows of the product's table.
/** The listing page number in the address (?strona=, legacy ?page=). */
function urlPageNow(): number {
    const params = new URLSearchParams(window.location.search);
    return Math.max(1, parseInt(params.get('strona') || params.get('page') || '1', 10) || 1);
}

export function pickCardSpecs(specifications: any, count: number, preferred: string): { name: string; value: string }[] {
    if (!Array.isArray(specifications) || count <= 0) return [];
    const rows = specifications
        .map((s: any) => ({ name: String(s?.name ?? s?.key ?? '').trim(), value: String(s?.value ?? '').trim() }))
        .filter((s) => s.name && s.value && !(s.name.startsWith('[') && s.name.endsWith(']')));
    const wanted = preferred.split(',').map((n) => n.trim().toLowerCase()).filter(Boolean);
    if (wanted.length === 0) return rows.slice(0, count);
    return wanted
        .map((w) => rows.find((r) => r.name.toLowerCase() === w))
        .filter((r): r is { name: string; value: string } => !!r)
        .slice(0, count);
}

// Internal Builder Card Component
function BuilderProductCard({ 
    product, 
    index, 
    props,
    siteConfig,
    onOpenLoginGate,
    onOpenNegotiate
}: { 
    product: any, 
    index: number, 
    props: ProductGridProps,
    siteConfig: any,
    onOpenLoginGate: () => void,
    onOpenNegotiate: (prod: any) => void
}) {
    const { t, currency, locale } = useTranslation();
    const { data: session } = useSession();
    // Logged-in B2B customers see net prices on cards, the same as on the product page (useProductPrice).
    const netDivisor = (session?.user as any)?.type === 'b2b' ? 1 + (product.tax_rate ?? 23) / 100 : 1;
    const formatCardPrice = (value: number, cur: string, loc: string) => formatPrice(value / netDivisor, cur, loc);
    const router = useRouter();
    const [isInCart, setIsInCart] = useState(false);
    const isFirstProductGrid = useIsFirstProductGrid(props.blockId);
    // The images a visitor sees first: one row on mobile (1 or 2 columns), the desktop row otherwise.
    const firstRowCount = props.columnsMobile === '1' ? 1 : Math.max(2, Number(props.columnsDesktop || 4));

    useEffect(() => {
        if (product.product_type === 'variable' || !product.is_one_off) return;
        const checkCart = () => {
            const cart = JSON.parse(localStorage.getItem('stag-cart') || '[]');
            setIsInCart(cart.some((item: any) => item.id === product.id && !item.variant_id));
        };
        checkCart();
        window.addEventListener('cart-updated', checkCart);
        return () => window.removeEventListener('cart-updated', checkCart);
    }, [product.id, product.product_type, product.is_one_off]);

    if (!product || !product.slug) return null;

    // Canonical product URL is /produkty/<slug> — no category path (see proxy.ts, which 301s
    // the old /produkty/<category>/<slug> form), so every link, the sitemap and the canonical
    // tag agree on one URL per product.
    const href = `/produkty/${product.slug}`;

    const {
        showImage = true,
        showTitle = true,
        showPrice = true,
        showBrand = true,
        brandPosition = 'above_price',
        brandLogoSize = 24,
        brandDisplay = 'logo',
        showButton = true,
        showDescription = false,
        showRating = false,
        showConditionBadge = false,
        showSavingsBadge = true,
        cardBorderColor = '#f3f4f6',
        cardHoverEffect = true,
        cardBorderRadius = 12,
        cardBorderWidth = 1,
    } = props;
    const ratingColor = props.ratingColor || props.customStyles?.ratingColor || '#f59e0b';
    
    const cs = props.customStyles || {};
    const titleColor = cs.titleColor || '#111827';
    const titleFontSize = cs.titleFontSize || 16;
    const titleAlign = cs.titleAlign || 'left';
    const priceColor = cs.priceColor || '#111827';
    const priceFontSize = cs.priceFontSize || 18;
    const priceAlign = cs.priceAlign || 'left';
    // Unset keeps the original red sale-price pill (bg-red-50 / text-red-700).
    const salePriceColor: string | undefined = cs.salePriceColor || undefined;
    const salePriceBg: string | undefined = cs.salePriceBg || undefined;
    const buttonAlign = cs.buttonAlign || 'center';
    // Card surface colors live in block.styles (like FeatureCards/Testimonials); unset keeps
    // the original white card / gray image well so existing grids render unchanged.
    const cardBg: string | undefined = cs.cardBg || undefined;
    const imageBg: string | undefined = cs.imageBg || undefined;
    const imageAspect: string | undefined = cs.imageAspect || undefined;
    const imagePadding = Number(cs.imagePadding) || 0;
    const specLabelColor = cs.specLabelColor || titleColor;
    const specValueColor = cs.specValueColor || titleColor;
    const specBorderColor = cs.specBorderColor || 'rgba(0,0,0,0.08)';
    // Same btn*/buyNow*/negotiate* field convention as ProductAddToCartBlock (product page) —
    // falls back to the old buttonBgColor/buttonTextColor/buttonBorderRadius fields (customStyles)
    // so pages saved before this convention existed keep their configured look. Note
    // buttonBorderRadius used to apply to *all three* buttons here, not just add-to-cart, so
    // buyNow/negotiate fall back to it too, not straight to the shared default.
    const buttonSettingsBase = {
        ...props,
        btnBgColor: props.btnBgColor ?? cs.buttonBgColor,
        btnTextColor: props.btnTextColor ?? cs.buttonTextColor,
        btnBorderRadius: props.btnBorderRadius ?? cs.buttonBorderRadius,
        buyNowBorderRadius: props.buyNowBorderRadius ?? cs.buttonBorderRadius,
        negotiateBorderRadius: props.negotiateBorderRadius ?? cs.buttonBorderRadius,
    };
    // Sizing fields (radius/border-width/padding/font-size/width) are responsive — the editor
    // writes them to styles[viewport] (merged into `cs` already), taking priority over both the
    // per-button settings value above and the older singular buttonBorderRadius fallback.
    const ACTION_BUTTON_SIZE_SUFFIXES = ['BorderRadius', 'BorderWidth', 'PaddingY', 'PaddingX', 'FontSize', 'Width'];
    const buttonSettings = { ...buttonSettingsBase };
    for (const prefix of ['btn', 'buyNow', 'negotiate']) {
        for (const suffix of ACTION_BUTTON_SIZE_SUFFIXES) {
            const k = `${prefix}${suffix}`;
            if (cs[k] !== undefined) (buttonSettings as any)[k] = cs[k];
        }
    }
    const btnIconType = props.btnIconType ?? props.iconType ?? 'none';
    const btnIconPosition = props.btnIconPosition ?? props.iconPosition ?? 'left';
    const btnIconEl = btnIconType !== 'none' ? <ProductActionIcon iconType={btnIconType} /> : null;

    const buyNowIconType = props.buyNowIconType ?? 'none';
    const buyNowIconPosition = props.buyNowIconPosition ?? 'left';
    const buyNowIconEl = buyNowIconType !== 'none' ? <ProductActionIcon iconType={buyNowIconType} /> : null;

    const negotiateIconType = props.negotiateIconType ?? 'none';
    const negotiateIconPosition = props.negotiateIconPosition ?? 'left';
    const negotiateIconEl = negotiateIconType !== 'none' ? <ProductActionIcon iconType={negotiateIconType} /> : null;

    const btnStyle = buildActionButtonStyle(buttonSettings, 'btn', ACTION_BUTTON_DEFAULTS.btn);
    const buyNowStyle = buildActionButtonStyle(buttonSettings, 'buyNow', ACTION_BUTTON_DEFAULTS.buyNow);
    const negotiateStyle = buildActionButtonStyle(buttonSettings, 'negotiate', ACTION_BUTTON_DEFAULTS.negotiate);
    const cardClsSuffix = `${props.blockId ?? 'grid'}-${product.id}`;
    const btnCls = `grid-btn-${cardClsSuffix}`;
    const buyNowCls = `grid-buynow-btn-${cardClsSuffix}`;
    const negotiateCls = `grid-negotiate-btn-${cardClsSuffix}`;
    const cardHoverCSS = [
        buildActionButtonHoverCSS(buttonSettings, 'btn', btnCls, ACTION_BUTTON_DEFAULTS.btn),
        buildActionButtonHoverCSS(buttonSettings, 'buyNow', buyNowCls, ACTION_BUTTON_DEFAULTS.buyNow),
        buildActionButtonHoverCSS(buttonSettings, 'negotiate', negotiateCls, ACTION_BUTTON_DEFAULTS.negotiate),
    ].join('\n');

    // Savings badge styles (Oszczędzasz)
    const savingsColor = cs.savingsColor || props.savingsColor || '#db2777';
    const savingsNoBg = props.savingsNoBg === true || cs.savingsNoBg === true;
    const savingsBgColor = savingsNoBg ? 'transparent' : (cs.savingsBgColor || props.savingsBgColor || '#fdf2f8');
    const savingsFontSize = cs.savingsFontSize || props.savingsFontSize || 12;
    const savingsFontWeight = cs.savingsFontWeight || props.savingsFontWeight || '900';
    const savingsAlign = cs.savingsAlign || props.savingsAlign || 'left';
    const savingsBorderRadius = cs.savingsBorderRadius !== undefined ? cs.savingsBorderRadius : (props.savingsBorderRadius !== undefined ? props.savingsBorderRadius : 6);
    const savingsBorderWidth = cs.savingsBorderWidth !== undefined ? cs.savingsBorderWidth : (props.savingsBorderWidth || 0);
    const savingsBorderColor = cs.savingsBorderColor || props.savingsBorderColor || 'transparent';
    const savingsLabel = props.savingsLabel || t('product.save_amount', 'Oszczędzasz');

    const savingsBadgeStyle: React.CSSProperties = {
        color: savingsColor,
        backgroundColor: savingsBgColor,
        fontSize: `${savingsFontSize}px`,
        fontWeight: savingsFontWeight,
        borderRadius: `${savingsBorderRadius}px`,
        borderWidth: savingsBorderWidth > 0 ? `${savingsBorderWidth}px` : undefined,
        borderColor: savingsBorderWidth > 0 ? savingsBorderColor : undefined,
        borderStyle: savingsBorderWidth > 0 ? 'solid' : undefined,
        display: 'inline-block',
        padding: savingsNoBg && savingsBorderWidth === 0 ? '0' : '2px 8px',
    };

    return (
        <IntentLink href={href} className="group block h-full">
            <div 
                className={cn(
                    "flex flex-col h-full transition-[transform,box-shadow] duration-300 relative overflow-hidden",
                    !cardBg && "bg-white",
                    cardHoverEffect && "hover:shadow-lg hover:-translate-y-1"
                )}
                style={{
                    backgroundColor: cardBg,
                    borderRadius: `${cardBorderRadius}px`,
                    borderWidth: `${cardBorderWidth}px`,
                    borderColor: cardBorderColor,
                    borderStyle: cardBorderWidth > 0 ? 'solid' : 'none'
                }}
            >
                {/* Dynamic Element Ordering according to elementOrder */}
                {(() => {
                    const elementOrder: ProductElementOrderItem[] = resolveProductElementOrder(props);
                    const flatElementKeys = flattenProductElementOrder(elementOrder);
                    const brandInOrder = flatElementKeys.includes('brand');

                    const renderImage = () => showImage && (
                        <div
                            key="image"
                            className={cn(
                                "aspect-square flex items-center justify-center text-gray-300 relative overflow-hidden",
                                !imageBg && (props.imageFit === 'contain' ? 'bg-transparent' : 'bg-gray-50'),
                                cardBorderWidth > 0 && "border-b border-gray-100"
                            )}
                            style={{ backgroundColor: imageBg, aspectRatio: imageAspect, borderBottomColor: cardBg ? cardBorderColor : undefined }}
                        >
                            {showConditionBadge && product.condition && (
                                <span className={`absolute top-2 left-2 z-10 px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-widest shadow-sm product-badge-${product.condition}`}>
                                    {t(`product.condition.${product.condition}`, product.condition)}
                                </span>
                            )}
                            <WishlistHeart productId={product.id} preview={props.isAdmin} />
                            {(() => {
                                const sortedImages: any[] = Array.isArray(product.images)
                                    ? [...product.images].sort((a: any, b: any) => {
                                        if (a.is_main && !b.is_main) return -1;
                                        if (!a.is_main && b.is_main) return 1;
                                        const sortA = a.sort_order ?? a.order ?? 0;
                                        const sortB = b.sort_order ?? b.order ?? 0;
                                        if (sortA !== sortB) return sortA - sortB;
                                        return (a.id || 0) - (b.id || 0);
                                    })
                                    : [];

                                const mainImage = sortedImages[0];
                                const hoverExplicitImage = sortedImages.find((img: any) => img.is_hover_image);
                                const secondaryImage = hoverExplicitImage || (sortedImages.length > 1 ? sortedImages[1] : null);
                                const hoverEffect = props.imageHoverEffect || 'second_image';

                                if (mainImage) {
                                    const url = toOptimizableSrc(getAbsoluteMediaUrl(mainImage.path));
                                    const secondaryUrl = (hoverEffect === 'second_image' || hoverEffect === 'gallery_dots') && secondaryImage
                                        ? toOptimizableSrc(getAbsoluteMediaUrl(secondaryImage.path))
                                        : null;

                                    // No floor on the desktop share: a 33vw minimum overstated the real
                                    // width on any grid with 4+ columns (the default is 4 → 25vw actual),
                                    // pushing Next's srcset to the next deviceSizes bucket up (640 instead
                                    // of 375) for an image displayed at a quarter of that.
                                    const imageSizes = props.columnsMobile === '1'
                                        ? `(max-width: 640px) 100vw, (max-width: 1024px) 50vw, ${Math.round(100 / Number(props.columnsDesktop || 4))}vw`
                                        : `(max-width: 640px) 50vw, (max-width: 1024px) 50vw, ${Math.round(100 / Number(props.columnsDesktop || 4))}vw`;

                                    // Unset (every page saved before this setting existed, and every new
                                    // grid a merchant hasn't touched this dropdown on) must default to
                                    // optimized — that was the actual behavior pre-imageQuality and is
                                    // what Core Web Vitals across every tenant were tuned against. 'high'
                                    // is an explicit opt-in for merchants who want raw 1:1 sharpness over
                                    // load time, not the fallback for "not decided yet".
                                    const isUnoptimized = props.imageQuality === 'high';

                                    return (
                                        <>
                                            <Image
                                                src={url}
                                                alt={product.name}
                                                fill
                                                unoptimized={isUnoptimized}
                                                quality={isUnoptimized ? undefined : 88}
                                                priority={props.isFirstBlock === true && index < firstRowCount}
                                                loading={props.isFirstBlock !== true && isFirstProductGrid && index < firstRowCount ? 'eager' : undefined}
                                                sizes={isUnoptimized ? undefined : imageSizes}
                                                style={imagePadding ? { padding: imagePadding } : undefined}
                                                className={cn(
                                                    "grid-product-img transition-all duration-500",
                                                    secondaryUrl && "group-hover:opacity-0",
                                                    hoverEffect === 'zoom' && "group-hover:scale-110",
                                                    hoverEffect === 'none' && "transition-none",
                                                    props.imageFit === 'contain' ? "object-contain" : "object-cover"
                                                )}
                                            />
                                            {secondaryUrl && (
                                                <Image
                                                    src={secondaryUrl}
                                                    alt={`${product.name} - widok 2`}
                                                    fill
                                                    unoptimized={isUnoptimized}
                                                    quality={isUnoptimized ? undefined : 88}
                                                    sizes={isUnoptimized ? undefined : imageSizes}
                                                    style={imagePadding ? { padding: imagePadding } : undefined}
                                                    // Hidden where nothing can hover (phones): a lazy image that isn't
                                                    // rendered is never downloaded — it used to double every card's
                                                    // image bytes on mobile for a picture only a mouse can reveal.
                                                    className={cn(
                                                        "grid-product-img absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:scale-105 [@media(hover:none)]:hidden",
                                                        props.imageFit === 'contain' ? "object-contain" : "object-cover"
                                                    )}
                                                />
                                            )}
                                            {hoverEffect === 'gallery_dots' && sortedImages.length > 1 && (
                                                <div className="absolute bottom-2 left-0 right-0 z-10 flex justify-center items-center gap-1.5 opacity-70 group-hover:opacity-100 transition-opacity">
                                                    {sortedImages.slice(0, 5).map((_, dotIdx) => (
                                                        <span
                                                            key={dotIdx}
                                                            className={cn(
                                                                "w-1.5 h-1.5 rounded-full transition-all shadow-xs",
                                                                dotIdx === 0 ? "bg-white ring-1 ring-black/20 scale-125" : "bg-white/60"
                                                            )}
                                                        />
                                                    ))}
                                                </div>
                                            )}
                                        </>
                                    );
                                }
                                return <ImageIcon className="w-10 h-10 opacity-30 group-hover:scale-110 transition-transform duration-500" />;
                            })()}
                        </div>
                    );

                    const renderTitle = () => showTitle && (
                        <h3 
                            key="title"
                            className="font-bold transition-colors line-clamp-2 leading-tight"
                            style={{
                                color: titleColor,
                                fontSize: `${titleFontSize}px`,
                                textAlign: titleAlign
                            }}
                        >
                            {product.name}
                        </h3>
                    );

                    const renderSpecs = () => {
                        if (!props.showSpecs) return null;
                        const specs = pickCardSpecs(product.specifications, Number(props.specsCount) || 3, props.specFields || '');
                        if (specs.length === 0) return null;
                        return (
                            <dl
                                key="specs"
                                className="grid gap-x-3 border-y py-2.5"
                                style={{ gridTemplateColumns: `repeat(${specs.length}, minmax(0, 1fr))`, borderColor: specBorderColor }}
                            >
                                {specs.map((spec) => (
                                    <div key={spec.name} className="min-w-0 flex flex-col">
                                        <dt className="order-last text-[10px] uppercase tracking-wider leading-tight break-words mt-1 opacity-60" style={{ color: specLabelColor }}>{spec.name}</dt>
                                        <dd className="text-sm font-bold leading-tight truncate" style={{ color: specValueColor }} title={spec.value}>{spec.value}</dd>
                                    </div>
                                ))}
                            </dl>
                        );
                    };

                    const renderRating = () => showRating && product.rating != null && product.rating > 0 && (
                        <div key="rating" className="flex items-center gap-1">
                            <StarRating value={product.rating} color={ratingColor} />
                        </div>
                    );

                    const renderDescription = () => showDescription && product.short_description && (
                        <p key="description" className="text-xs text-gray-500 line-clamp-2 leading-relaxed">
                            {stripHtml(product.short_description)}
                        </p>
                    );

                    const brandMark = showBrand ? (
                        <ProductBrandMark
                            brand={product.brand}
                            display={brandDisplay}
                            logoSize={Number(cs.brandLogoSize) || brandLogoSize}
                            color={cs.brandColor}
                            fontSize={cs.brandFontSize}
                        />
                    ) : null;
                    const hasBrandMark = !!product.brand && (brandDisplay === 'logo' ? !!product.brand.logo_url : !!(product.brand.logo_url || product.brand.name));
                    const brandLogoEl = brandMark && hasBrandMark ? <div key="brand" className="shrink-0 flex items-center">{brandMark}</div> : null;
                    const renderBrand = () => brandLogoEl;
                    // A [price, brand] group puts the brand at the end of the price's own line
                    // (not centred against the whole price block, which with a sale also holds
                    // the savings badge). On a card too narrow for both it drops below the price —
                    // justify-between keeps a lone wrapped brand at the start of its line.
                    const brandTrailsPrice = !!brandLogoEl && showPrice && elementOrder.some(
                        (item) => Array.isArray(item) && item.includes('price') && item.includes('brand')
                    );
                    // Legacy behaviour for pages saved before "brand" became a real elementOrder
                    // entry: brandPosition still glues the logo above/beside the price block.
                    // Once "brand" is placed explicitly in elementOrder (new pages, or migrated
                    // brandPosition:'beside_price' pages — see resolveProductElementOrder), it
                    // renders wherever it was placed instead, so this legacy path is skipped.
                    const legacyBrandLogoEl = !brandInOrder ? brandLogoEl : null;
                    const brandBesidePrice = !!legacyBrandLogoEl && brandPosition === 'beside_price';
                    const withBrandInline = (row: React.ReactNode) => (
                        brandBesidePrice
                            ? <div className="flex flex-row items-center gap-2">{legacyBrandLogoEl}{row}</div>
                            : brandTrailsPrice
                                ? <div className="flex flex-row flex-wrap items-center justify-between gap-x-2 gap-y-1"><div className="min-w-0">{row}</div>{brandLogoEl}</div>
                                : row
                    );

                    // A per-kg service shows its price per kg, as on the product page.
                    const pricingUnit = servicePricingType(product);
                    const perKgSuffix = (pricingUnit === 'weight' || pricingUnit === 'area') && (
                        <span className="text-[0.6em] font-semibold opacity-60 ml-1">
                            {pricingUnit === 'area' ? t('product.per_m2', '/ m²') : t('product.per_kg', '/ kg')}
                        </span>
                    );
                    const netSuffix = netDivisor !== 1 && (
                        <span className="text-[0.6em] font-semibold opacity-60 ml-1">{t('product.b2b_net', 'netto')}</span>
                    );
                    const renderPrice = () => showPrice && (
                        <div key="price" className="flex flex-col gap-2" style={{ color: priceColor, textAlign: priceAlign }}>
                            {legacyBrandLogoEl && !brandBesidePrice && legacyBrandLogoEl}
                            {product.price_on_request ? (
                                // "Cena na zapytanie" — the label sits where the price would.
                                withBrandInline(
                                    <span className="font-bold" style={{ fontSize: `${Math.round(priceFontSize * 0.85)}px` }}>
                                        {priceOnRequestLabel(product, t)}
                                    </span>
                                )
                            ) : product.product_type === 'bundle' ? (
                                <div className="space-y-1">
                                    {withBrandInline(
                                        <div className="flex items-center gap-2" style={{ justifyContent: priceAlign === 'center' ? 'center' : priceAlign === 'right' ? 'flex-end' : 'flex-start' }}>
                                            <span className="font-black text-slate-900" style={{ fontSize: `${priceFontSize}px` }}>
                                                {formatCardPrice(product.sales_price ?? product.price ?? 0, currency, locale)}
                                            </span>
                                            {(product.items_regular_price_sum || product.price) > (product.sales_price ?? product.price ?? 0) && (
                                                <span className="text-[0.75em] text-slate-400 line-through">
                                                    {formatCardPrice(product.items_regular_price_sum || product.price, currency, locale)}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    {showSavingsBadge && (product.savings_amount || ((product.items_regular_price_sum || 0) - (product.sales_price ?? product.price ?? 0))) > 0 && (
                                        <div style={{ textAlign: savingsAlign }}>
                                            <p style={savingsBadgeStyle}>
                                                {savingsLabel} {formatCardPrice(product.savings_amount || ((product.items_regular_price_sum || 0) - (product.sales_price ?? product.price ?? 0)), currency, locale)}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            ) : product.product_type === 'variable' || usesConfigurator(product) ? (
                                withBrandInline(
                                    <span className="font-black" style={{ fontSize: `${priceFontSize}px` }}>
                                        <span className="text-[0.65em] font-normal opacity-70 mr-1.5 uppercase tracking-wide">{t('product.from', 'od')}</span>
                                        {formatCardPrice(product.sales_price ?? product.price, currency, locale)}
                                        {perKgSuffix}{netSuffix}
                                    </span>
                                )
                            ) : product.sales_price !== null ? (
                                <div className="space-y-1">
                                    {brandTrailsPrice ? (
                                        // Sale price and brand share the first line; the struck-through
                                        // regular price always takes the next one, so the brand stays
                                        // beside the price instead of being the first thing to wrap.
                                        <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5">
                                            <span
                                                className={cn("font-black px-2 py-0.5 rounded-md whitespace-nowrap", !salePriceBg && 'bg-red-50', !salePriceColor && 'text-red-700')}
                                                style={{ fontSize: `${priceFontSize}px`, color: salePriceColor, backgroundColor: salePriceBg }}
                                            >{formatCardPrice(product.sales_price, currency, locale)}</span>
                                            {brandLogoEl}
                                            <span className="basis-full opacity-50 line-through whitespace-nowrap" style={{ fontSize: `${Math.round(priceFontSize * 0.7)}px` }}>{formatCardPrice(product.price, currency, locale)}</span>
                                        </div>
                                    ) : withBrandInline(
                                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5" style={{ justifyContent: priceAlign === 'center' ? 'center' : priceAlign === 'right' ? 'flex-end' : 'flex-start' }}>
                                            <span
                                                className={cn("font-black px-2 py-0.5 rounded-md whitespace-nowrap", !salePriceBg && 'bg-red-50', !salePriceColor && 'text-red-700')}
                                                style={{ fontSize: `${priceFontSize}px`, color: salePriceColor, backgroundColor: salePriceBg }}
                                            >{formatCardPrice(product.sales_price, currency, locale)}</span>
                                            <span className="text-[0.7em] opacity-50 line-through whitespace-nowrap">{formatCardPrice(product.price, currency, locale)}</span>
                                        </div>
                                    )}
                                    {showSavingsBadge && product.price > product.sales_price && (
                                        <div style={{ textAlign: savingsAlign }}>
                                            <p style={savingsBadgeStyle}>
                                                {savingsLabel} {formatCardPrice(product.price - product.sales_price, currency, locale)}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                withBrandInline(
                                    <span className="font-black whitespace-nowrap" style={{ fontSize: `${priceFontSize}px` }}>{formatCardPrice(product.price, currency, locale)}{perKgSuffix}{netSuffix}</span>
                                )
                            )}
                        </div>
                    );

                    // A numbered-seat ticket can't be added to the cart from the grid — the customer
                    // has to open the product page and pick a seat first. General-admission tickets
                    // open it too (cardOpensProductPage) — the event date is picked there.
                    const isSeatMapTicket = product.product_type === 'ticket' && !!product.has_seat_map;
                    // A subscription plan (Stripe Billing, no cart involved at all) or a service
                    // with a "subscribe and save" tier can't be quick-added here — there's no room
                    // on a card to pick a commitment tier/payment mode. Same treatment as
                    // variable/pick_pack/configurable below: the button just falls through to the
                    // card's own Link and opens the product page instead.
                    const needsSubscriptionPurchase = getProductPrimaryAction(product).type === 'view_product';
                    // Something to pick on the product page first (variant, options, seat, weight,
                    // tier): the card's one button opens it, with no "Kup teraz" next to it.
                    const opensProductPage = cardOpensProductPage(product);

                    const renderButton = () => {
                        if (!showButton) return null;

                        const showAddToCart = props.showAddToCart !== false;
                        const showBuyNow = props.showBuyNow === true && !opensProductPage;
                        const showNegotiate = props.showNegotiate === true && !isSeatMapTicket && isNegotiationAllowedForProduct(siteConfig, product);
                        const buttonsOrder = isSeatMapTicket
                            ? ['addToCart']
                            : Array.isArray(props.buttonsOrder) && props.buttonsOrder.length > 0
                                ? props.buttonsOrder
                                : ['addToCart', 'buyNow', 'negotiate'];

                        return (
                            <div key="button" className="mt-auto pt-1 flex flex-col gap-1.5 w-full">
                                <style>{cardHoverCSS}</style>
                                {buttonsOrder.map((bKey) => {
                                    if (bKey === 'addToCart' && showAddToCart) {
                                        return (
                                            <Button
                                                key="addToCart"
                                                className={cn("w-full font-bold shadow-none hover:shadow-md transition-all group/btn text-[10px] xs:text-xs sm:text-sm px-1 sm:px-4 h-auto min-h-[32px] sm:min-h-[38px] whitespace-normal leading-tight gap-1.5", btnCls)}
                                                style={{
                                                    ...btnStyle,
                                                    justifyContent: buttonAlign === 'left' ? 'flex-start' : buttonAlign === 'right' ? 'flex-end' : 'center'
                                                }}
                                                disabled={!opensProductPage && product.is_one_off && isInCart}
                                                onClick={(e) => {
                                                    if (opensProductPage) return;
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    if (product.is_one_off && isInCart) {
                                                        toast.error(t('product.one_off_limit', 'Ten produkt można dodać do koszyka tylko raz.'));
                                                        return;
                                                    }
                                                    try {
                                                        const cart = JSON.parse(localStorage.getItem('stag-cart') || '[]');
                                                        const existingItemIndex = cart.findIndex((item: any) => item.id === product.id && !item.variant_id);
                                                        const mainImage = product.images?.find((img: any) => img.is_main) || product.images?.[0];
                                                        const imageUrl = mainImage ? getPublicMediaUrl(mainImage.path) : null;
                                                        if (existingItemIndex > -1) {
                                                            if (product.is_one_off) {
                                                                toast.error(t('product.one_off_limit', 'Ten produkt można dodać do koszyka tylko raz.'));
                                                                return;
                                                             }
                                                            cart[existingItemIndex].quantity += 1;
                                                            cart[existingItemIndex].image = imageUrl;
                                                        } else {
                                                            const activePrice = product.sales_price !== null && product.sales_price !== undefined ? product.sales_price : product.price;
                                                            cart.push({
                                                                id: product.id,
                                                                product_type: product.product_type,
                                                                name: product.name,
                                                                price: activePrice,
                                                                image: imageUrl,
                                                                slug: product.slug,
                                                                quantity: 1,
                                                                is_one_off: !!product.is_one_off
                                                            });
                                                        }
                                                        localStorage.setItem('stag-cart', JSON.stringify(cart));
                                                        window.dispatchEvent(new Event('cart-updated'));
                                                        trackAddToCart(product, null, 1, currency || 'PLN');
                                                        toast.success(t('product.add_to_cart_success', 'Dodano do koszyka!'));
                                                    } catch {
                                                        toast.error(t('product.add_to_cart_error', 'Błąd podczas dodawania do koszyka'));
                                                    }
                                                }}
                                            >
                                                {btnIconPosition === 'left' && btnIconEl}
                                                {isSeatMapTicket
                                                    ? t('product.select_seat', 'Wybierz miejsce')
                                                    : product.product_type === 'ticket'
                                                        ? t('product.select_ticket', 'Wybierz bilet')
                                                    : product.product_type === 'service' && usesConfigurator(product)
                                                        ? t('product.select_options', 'Wybierz opcje')
                                                    : servicePricingType(product) === 'weight' && !product.has_subscription_tiers
                                                        ? t('product.enter_weight', 'Podaj wagę')
                                                    : servicePricingType(product) === 'area' && !product.has_subscription_tiers
                                                        ? t('product.enter_dimensions', 'Podaj wymiary')
                                                    : needsSubscriptionPurchase
                                                        ? (props.viewOfferText || t('product.view_offer', 'Zobacz ofertę'))
                                                        : product.product_type === 'pick_pack'
                                                        ? (product.pickpack_config?.cta_label || t('product.configure_bundle', 'Skonfiguruj zestaw'))
                                                        : product.product_type === 'configurable'
                                                            ? t('product.configure', 'Konfiguruj')
                                                            : product.product_type === 'variable'
                                                                ? t('product.select_options', 'Wybierz opcje')
                                                                : product.product_type === 'bundle'
                                                                    ? (props.buttonText || t('product.add_bundle_to_cart', 'Dodaj zestaw do koszyka'))
                                                                    : (product.is_one_off && isInCart
                                                                        ? t('product.already_in_cart', 'W koszyku')
                                                                        : (props.buttonText || t('product.add_to_cart', 'Dodaj do koszyka')))}
                                                {btnIconPosition === 'right' && btnIconEl}
                                            </Button>
                                        );
                                    }

                                    if (bKey === 'buyNow' && showBuyNow) {
                                        return (
                                            <Button
                                                key="buyNow"
                                                className={cn("w-full font-bold shadow-none hover:shadow-md transition-all text-[10px] xs:text-xs sm:text-sm px-1 sm:px-4 h-auto min-h-[32px] sm:min-h-[38px] gap-1.5", buyNowCls)}
                                                style={buyNowStyle}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    if (opensProductPage) {
                                                        router.push(`/produkty/${product.slug}`);
                                                        return;
                                                    }
                                                    try {
                                                        const cart = JSON.parse(localStorage.getItem('stag-cart') || '[]');
                                                        const existingItemIndex = cart.findIndex((item: any) => item.id === product.id && !item.variant_id);
                                                        const mainImage = product.images?.find((img: any) => img.is_main) || product.images?.[0];
                                                        const imageUrl = mainImage ? getPublicMediaUrl(mainImage.path) : null;
                                                        if (existingItemIndex > -1) {
                                                            cart[existingItemIndex].quantity += 1;
                                                        } else {
                                                            const activePrice = product.sales_price !== null && product.sales_price !== undefined ? product.sales_price : product.price;
                                                            cart.push({
                                                                id: product.id,
                                                                product_type: product.product_type,
                                                                name: product.name,
                                                                price: activePrice,
                                                                image: imageUrl,
                                                                slug: product.slug,
                                                                quantity: 1,
                                                                is_one_off: !!product.is_one_off
                                                            });
                                                        }
                                                        localStorage.setItem('stag-cart', JSON.stringify(cart));
                                                        window.dispatchEvent(new Event('cart-updated'));
                                                        router.push('/zamowienie');
                                                    } catch {
                                                        toast.error(t('product.add_to_cart_error', 'Błąd podczas dodawania do koszyka'));
                                                    }
                                                }}
                                            >
                                                {buyNowIconPosition === 'left' && buyNowIconEl}
                                                {props.buyNowText || t('product.buy_now', 'Kup teraz')}
                                                {buyNowIconPosition === 'right' && buyNowIconEl}
                                            </Button>
                                        );
                                    }

                                    if (bKey === 'negotiate' && showNegotiate) {
                                        return (
                                            <Button
                                                key="negotiate"
                                                className={cn("w-full font-semibold shadow-none hover:shadow-xs transition-all text-[10px] xs:text-xs sm:text-sm px-1 sm:px-4 h-auto min-h-[30px] sm:min-h-[36px] gap-1.5", negotiateCls)}
                                                style={negotiateStyle}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    if (!session?.user) {
                                                        onOpenLoginGate();
                                                    } else {
                                                        onOpenNegotiate(product);
                                                    }
                                                }}
                                            >
                                                {negotiateIconPosition === 'left' && negotiateIconEl}
                                                {props.negotiateText || t('product.negotiate_price', 'Negocjuj cenę')}
                                                {negotiateIconPosition === 'right' && negotiateIconEl}
                                            </Button>
                                        );
                                    }

                                    return null;
                                })}
                            </div>
                        );
                    };

                    const renderByKey = (key: string): React.ReactNode => {
                        if (key === 'image') return renderImage();
                        if (key === 'title') return renderTitle();
                        if (key === 'specs') return renderSpecs();
                        if (key === 'rating') return renderRating();
                        if (key === 'description') return renderDescription();
                        if (key === 'price') return renderPrice();
                        if (key === 'button') return renderButton();
                        if (key === 'brand') return renderBrand();
                        return null;
                    };
                    // "brand" is meant to hug the width of its own logo even inside a group row —
                    // every other groupable element (e.g. price) takes the remaining space.
                    const groupItemClass = (key: string) => (key === 'brand' ? 'shrink-0' : 'flex-1 min-w-0');

                    const isImageFirst = elementOrder[0] === 'image';

                    return (
                        <>
                            {isImageFirst && renderImage()}
                            <div className="p-4 flex flex-col flex-1 gap-3">
                                {elementOrder.map((item, i) => {
                                    if (Array.isArray(item)) {
                                        if (brandTrailsPrice && item.includes('price') && item.includes('brand')) {
                                            const rest = item.filter((key) => key !== 'brand');
                                            if (rest.length === 1) return renderByKey(rest[0]);
                                            item = rest;
                                        }
                                        return (
                                            <div key={`group-${i}`} className="flex flex-row items-center gap-2">
                                                {item.map((key) => (
                                                    <div key={key} className={groupItemClass(key)}>{renderByKey(key)}</div>
                                                ))}
                                            </div>
                                        );
                                    }
                                    if (item === 'image' && isImageFirst) return null;
                                    return renderByKey(item);
                                })}
                            </div>
                        </>
                    );
                })()}
            </div>
        </IntentLink>
    );
}

let cachedFallbackCategoryId: string | null = null;

export function ProductGrid(props: ProductGridProps) {
    const {
        queryType = 'latest',
        relatedFallback = 'none',
        categoryId,
        brandSlugs,
        manualProductIds = [],
        activeCategory,
        columnsDesktop = '4',
        columnsMobile = '2',
        editorViewport,
        limit = 4,
        paginationEnabled = false,
        paginationPosition = 'center',
        paginationStyle = 'numbers',
        initialProducts
    } = props;
    // Themes author this under block.styles.gap (customStyles), not settings.
    const gridGap = props.customStyles?.gap !== undefined ? `${props.customStyles.gap}px` : undefined;

    const { currency, t } = useTranslation();

    // Determine active columns based on editorViewport prop (editor mode) or CSS media queries (published)
    const activeColumns = editorViewport
        ? (editorViewport === 'mobile' ? columnsMobile : columnsDesktop)
        : null;
    const gridColsStyle = activeColumns
        ? { gridTemplateColumns: `repeat(${activeColumns}, minmax(0, 1fr))` }
        : undefined;
    // columnsDesktop/columnsMobile arrive as either a number or a string depending on
    // whether the page JSON was hand-authored or emitted by shopstag_themes (which writes
    // real numbers) — compare numerically so both forms work.
    const columnsDesktopNum = Number(columnsDesktop);
    const columnsMobileNum = Number(columnsMobile);
    const gridColsClass = !activeColumns
        ? cn(
            columnsMobileNum === 1 ? 'grid-cols-1' : 'grid-cols-2',
            columnsDesktopNum === 2 ? 'md:grid-cols-2' :
            columnsDesktopNum === 3 ? 'md:grid-cols-3' :
            columnsDesktopNum === 5 ? 'md:grid-cols-5' :
            columnsDesktopNum === 6 ? 'md:grid-cols-6' : 'md:grid-cols-4'
          )
        : '';
    const storefrontGridVars = !editorViewport ? {
        '--product-grid-mobile-columns': Math.max(1, columnsMobileNum || 2),
        '--product-grid-desktop-columns': Math.max(1, columnsDesktopNum || 4),
        '--product-grid-column-gap': gridGap || '1.5rem',
        '--product-grid-row-gap': gridGap || '2rem',
    } as React.CSSProperties : undefined;

    const isCurrentCategory = queryType === 'current_category';
    const initialItems = initialProducts && typeof initialProducts === 'object' && 'items' in initialProducts
        ? (initialProducts as any).items
        : (Array.isArray(initialProducts) ? initialProducts : []);

    const initialTotal = initialProducts && typeof initialProducts === 'object' && 'total' in initialProducts
        ? (initialProducts as any).total
        : (Array.isArray(initialProducts) ? initialProducts.length : 0);

    const initialPage = initialProducts && typeof initialProducts === 'object' && 'page' in initialProducts
        ? (initialProducts as any).page
        : 1;

    const initialPageSize = initialProducts && typeof initialProducts === 'object' && 'pageSize' in initialProducts
        ? (initialProducts as any).pageSize
        : (limit || 24);

    const [allProducts, setAllProducts] = useState<any[]>(initialItems);
    const [serverTotal, setServerTotal] = useState<number>(initialTotal);
    const [loading, setLoading] = useState(!initialProducts);
    const [currentPage, setCurrentPage] = useState(initialPage);
    // Skips the first client fetch when the server already prefetched this block's data
    // (see serverPrefetch.ts) — avoids a duplicate request and the loading-skeleton flash.
    const skipNextFetchRef = useRef(!!initialProducts);
    const gridContainerRef = useRef<HTMLDivElement>(null);

    const catalogCtx = useCatalogContext();
    const catalogFiltersStateKey = catalogCtx ? filtersKey(catalogCtx.filters) : '';
    
    // On a product page, the product the 'related' query is about. usePathname() from the market
    // navigation returns the app path — /en/products/x and /products/x both read as /produkty/x
    // (the raw window path never matched 'produkty' under a localized route).
    const pathname = usePathname();
    const urlSlug = pathname.match(/^\/produkty\/(?:.+\/)?([^/]+)$/)?.[1] ?? '';

    const manualProductIdsKey = manualProductIds.join(',');
    const brandSlugsKey = (brandSlugs || []).join(',');
    const itemsPerPage = isCurrentCategory ? (initialPageSize || limit || 24) : limit;

    useEffect(() => {
        if (skipNextFetchRef.current) {
            skipNextFetchRef.current = false;
            return;
        }
        const loadProducts = async () => {
            setLoading(true);

            // Manual selection: fetch exactly the curated IDs and reorder the response to
            // match the merchant's saved order — the API's `ids` filter doesn't guarantee
            // result order, only which rows come back (and still applies every visibility
            // filter, e.g. status/B2B, same as any other queryType).
            if (queryType === 'manual') {
                if (manualProductIds.length === 0) {
                    setAllProducts([]);
                    setLoading(false);
                    setCurrentPage(1);
                    return;
                }
                try {
                    const data = await fetchAPI(`/products?ids=${manualProductIds.join(',')}&limit=${manualProductIds.length}&currency=${currency}`);
                    if (Array.isArray(data)) {
                        const byId = new Map(data.map((p: any) => [p.id, p]));
                        setAllProducts(manualProductIds.map(id => byId.get(id)).filter(Boolean) as any[]);
                    }
                } catch (error) {
                    console.error("Failed to load manually selected products for grid:", error);
                }
                setLoading(false);
                setCurrentPage(1);
                return;
            }

            const queryParams = new URLSearchParams();

            if (queryType === 'category' && categoryId) {
                queryParams.append('category_id', categoryId);
            } else if (queryType === 'current_category') {
                if (activeCategory?.is_all_products) {
                    // Do not filter by category_id to fetch all products
                } else {
                    let resolvedCategoryId = activeCategory?.id;
                    if (!resolvedCategoryId) {
                        if (cachedFallbackCategoryId) {
                            resolvedCategoryId = cachedFallbackCategoryId;
                        } else {
                            // Fallback for editor mode: find first category to populate preview
                            try {
                                const categories = await fetchAPI('/categories');
                                if (categories && categories.length > 0) {
                                    cachedFallbackCategoryId = categories[0].id;
                                    resolvedCategoryId = cachedFallbackCategoryId;
                                }
                            } catch (e) {
                                console.error("Failed to load fallback category inside editor:", e);
                            }
                        }
                    }

                    const subcatId = catalogCtx?.filters?.subcategoryId;
                    if (subcatId) {
                        queryParams.append('category_id', String(subcatId));
                    } else if (resolvedCategoryId) {
                        queryParams.append('category_id', String(resolvedCategoryId));
                    }
                }

                if (catalogCtx?.filters?.query?.trim()) {
                    queryParams.append('search', catalogCtx.filters.query.trim());
                }
                if (catalogCtx?.filters?.minPrice) {
                    queryParams.append('min_price', catalogCtx.filters.minPrice);
                }
                if (catalogCtx?.filters?.maxPrice) {
                    queryParams.append('max_price', catalogCtx.filters.maxPrice);
                }
                if (catalogCtx?.filters?.facets) {
                    appendFacetFilters(queryParams, catalogCtx.filters.facets);
                }
                if (catalogCtx?.sort) {
                    queryParams.append('sort', catalogCtx.sort);
                }
            }
            if (queryType === 'sale') {
                queryParams.append('is_sale', 'true');
            }
            if (queryType === 'bundle') {
                queryParams.append('product_type', 'bundle');
            }
            appendBlockBrandFilter(queryParams, queryType, brandSlugs);

            if (queryType === 'current_category') {
                const pageSize = itemsPerPage;
                const page = currentPage;
                const skip = (page - 1) * pageSize;

                queryParams.append('skip', String(skip));
                queryParams.append('limit', String(pageSize));
                queryParams.append('currency', currency);

                try {
                    const countParams = new URLSearchParams();
                    if (queryParams.has('category_id')) countParams.set('category_id', queryParams.get('category_id')!);
                    if (queryParams.has('search')) countParams.set('search', queryParams.get('search')!);
                    if (queryParams.has('min_price')) countParams.set('min_price', queryParams.get('min_price')!);
                    if (queryParams.has('max_price')) countParams.set('max_price', queryParams.get('max_price')!);
                    for (const [key, value] of queryParams.entries()) {
                        if (key.startsWith('f.')) countParams.set(key, value);
                    }

                    const [data, countRes] = await Promise.all([
                        fetchAPI(`/products?${queryParams.toString()}`),
                        fetchAPI(`/products/count?${countParams.toString()}`).catch(() => ({ total: 0 })),
                    ]);

                    if (data && Array.isArray(data)) {
                        setAllProducts(data);
                        const total = typeof countRes?.total === 'number' ? countRes.total : data.length;
                        setServerTotal(total);
                        catalogCtx?.setProductCount(total);
                    }
                } catch (error) {
                    console.error("Failed to load products for grid:", error);
                }

                setLoading(false);
                return;
            }

            // If pagination is enabled, we fetch more to simulate pagination client-side.
            // For 'latest' queryType cap at 4 pages — search covers the rest.
            const fetchLimit = activeCategory?.is_all_products
                ? 1000
                : (paginationEnabled
                    ? (queryType === 'latest' ? 4 * limit : 100)
                    : limit);
            queryParams.append('limit', String(fetchLimit));
            // Pass currency explicitly rather than relying on fetchAPI's stag_currency cookie
            // fallback: on a visitor's very first request that cookie doesn't exist yet, so the
            // header would be omitted and the backend would default to PLN even though `currency`
            // (from TranslationProvider, seeded from the shop's configured currency) is already EUR.
            queryParams.append('currency', currency);

            try {
                if (queryType === 'related' && urlSlug) {
                    const fallbackParams = relatedFallback !== 'none' ? `&fallback=${relatedFallback}&limit=${limit}` : '';
                    const data = await fetchAPI(`/products/${urlSlug}/related?currency=${currency}${fallbackParams}`);
                    if (data) setAllProducts(data);
                } else {
                    const data = await fetchAPI(`/products?${queryParams.toString()}`);
                    if (data) setAllProducts(data);
                }
            } catch (error) {
                console.error("Failed to load products for grid:", error);
            }
            
            setLoading(false);
            setCurrentPage(1); // Reset page on query change
        };
        loadProducts();
        // `currency` must stay a dependency: fetchAPI sends it as X-Currency (read from the
        // stag_currency cookie) and the backend's localize_product() converts price/sales_price
        // server-side. Without this, switching currency only re-renders the `{currency}` label
        // next to a still-PLN number that was fetched before the switch — the bug a tester
        // reported as "price doesn't convert, it just changes the currency sign".
    }, [
        queryType,
        relatedFallback,
        categoryId,
        brandSlugsKey,
        limit,
        paginationEnabled,
        activeCategory?.id,
        activeCategory?.is_all_products,
        currency,
        manualProductIdsKey,
        urlSlug,
        currentPage,
        catalogCtx?.filters?.query,
        catalogCtx?.filters?.minPrice,
        catalogCtx?.filters?.maxPrice,
        catalogCtx?.filters?.subcategoryId,
        catalogFiltersStateKey,
        catalogCtx?.sort,
    ]);

    // When filters change in current_category, reset page to 1
    const prevFiltersKey = useRef(
        catalogCtx ? `${catalogFiltersStateKey}|${catalogCtx.sort}` : ''
    );
    useEffect(() => {
        if (!isCurrentCategory || !catalogCtx) return;
        const currentKey = `${catalogFiltersStateKey}|${catalogCtx.sort}`;
        if (prevFiltersKey.current !== currentKey) {
            prevFiltersKey.current = currentKey;
            if (currentPage !== 1) {
                setCurrentPage(1);
                if (typeof window !== 'undefined') {
                    const url = new URL(window.location.href);
                    url.searchParams.delete('strona');
                    url.searchParams.delete('page');
                    window.history.pushState({}, '', `${url.pathname}${url.search}`);
                }
            }
        }
    }, [isCurrentCategory, catalogCtx?.filters, catalogCtx?.sort, catalogFiltersStateKey, currentPage]);

    // The page number lives in ?strona=, written with history.pushState (no Next navigation), so
    // coming back from a product restores this route's cached render — page 1's products under a
    // ?strona=N address (shoppers landed on page 1 again). Follow the address on mount and on
    // back/forward; a changed currentPage refetches that page.
    useEffect(() => {
        if (!isCurrentCategory || typeof window === 'undefined') return;
        const followUrl = () => {
            const urlPage = urlPageNow();
            setCurrentPage(urlPage);
            catalogCtx?.setCurrentPage(urlPage);
        };
        followUrl();
        window.addEventListener('popstate', followUrl);
        return () => window.removeEventListener('popstate', followUrl);
    }, [isCurrentCategory]); // eslint-disable-line react-hooks/exhaustive-deps
    // A page other than the rendered one is refetched right after mounting — restore after it lands.
    useListingScrollRestore(isCurrentCategory, loading, () => urlPageNow() !== currentPage);

    // Apply catalog filters from context (when catalog-filters block is used in the same template)
    const filteredProducts = useMemo(() => {
        if (isCurrentCategory) {
            return allProducts;
        }
        if (!catalogCtx) return allProducts;
        const { filters } = catalogCtx;
        return allProducts.filter((p) => {
            if (filters.query.trim()) {
                const q = filters.query.toLowerCase();
                if (![p.name, p.sku, p.short_description].join(' ').toLowerCase().includes(q)) return false;
            }
            const effectivePrice = p.sales_price ?? p.price;
            if (filters.minPrice !== '' && effectivePrice < parseFloat(filters.minPrice)) return false;
            if (filters.maxPrice !== '' && effectivePrice > parseFloat(filters.maxPrice)) return false;
            if (filters.subcategoryId !== null) {
                const inCat = p.categories?.some((c: any) => c.id === filters.subcategoryId) || p.category_id === filters.subcategoryId;
                if (!inCat) return false;
            }
            return true;
        });
    }, [isCurrentCategory, allProducts, catalogCtx?.filters]);

    // Report filtered count back to context so the catalog-filters block can display it
    useEffect(() => {
        if (catalogCtx && !isCurrentCategory) {
            catalogCtx.setProductCount(filteredProducts.length);
        }
    }, [isCurrentCategory, filteredProducts.length, catalogCtx]);

    // Must stay above the `loading`/empty-state early returns below — a hook placed after a
    // conditional return fires on some renders and not others (e.g. skipped entirely while
    // `loading` is true), which React rejects with error #310 ("rendered more hooks than during
    // the previous render") the moment the component transitions out of that branch.
    const siteConfig = useSiteConfig();
    const [showLoginGate, setShowLoginGate] = useState(false);
    const [negotiateProduct, setNegotiateProduct] = useState<any>(null);

    // Carousel: the shared builder Carousel. columnsDesktop/columnsMobile are the cards visible at
    // once unless the carousel's own per-view settings are set; no loop by default (as before).
    const isCarousel = props.layout === 'carousel';
    const carousel = carouselPropsFromSettings(props, {
        desktop: Math.max(1, columnsDesktopNum || 4),
        tablet: Math.min(Math.max(1, columnsDesktopNum || 4), 3),
        mobile: Math.max(1, columnsMobileNum || 2),
    }, { loop: false });
    const renderCarousel = (items: React.ReactNode[]) => (
        <Carousel
            perView={carousel.perView}
            gap={props.customStyles?.gap !== undefined ? Number(props.customStyles.gap) || 0 : 24}
            autoplayMs={carousel.autoplayMs}
            loop={carousel.loop}
            arrows={carousel.arrows}
            dots={carousel.dots}
            peek
            arrowPlacement="edge"
            arrowColors={{ background: props.customStyles?.arrowBg, color: props.customStyles?.arrowColor }}
            viewport={editorViewport}
            isAdmin={props.isAdmin}
            ariaLabel={t('product.carousel_label', 'Karuzela produktów')}
        >
            {items}
        </Carousel>
    );

    if (loading) {
        if (isCarousel) {
            return renderCarousel([...Array(Math.min(limit, 6))].map((_, i) => (
                <div key={i} className="flex flex-col gap-4 border border-gray-100 rounded-xl p-4 bg-white">
                    <Skeleton className="aspect-square w-full rounded-lg bg-gray-100" />
                    <Skeleton className="h-5 w-3/4 rounded bg-gray-100" />
                    <Skeleton className="h-5 w-1/3 rounded bg-gray-100 mt-auto" />
                </div>
            )));
        }
        return (
            <div
                className={cn("grid", !editorViewport && "storefront-product-grid", editorViewport && !gridGap && "gap-6", gridColsClass)}
                style={{ ...gridColsStyle, ...storefrontGridVars, ...(gridGap ? { gap: gridGap } : {}) }}
            >
                {[...Array(limit)].map((_, i) => (
                    <div key={i} className="flex flex-col gap-4 border border-gray-100 rounded-xl p-4 bg-white">
                        <Skeleton className="aspect-square w-full rounded-lg bg-gray-100" />
                        <Skeleton className="h-5 w-3/4 rounded bg-gray-100" />
                        <Skeleton className="h-5 w-1/3 rounded bg-gray-100 mt-auto" />
                        <Skeleton className="h-10 w-full rounded bg-gray-100 mt-2" />
                    </div>
                ))}
            </div>
        );
    }

    if (filteredProducts.length === 0) {
        // "Related"/cross-sell only makes sense when there's something to cross-sell —
        // an empty "brak produktów" block under a "Może Cię zainteresuje" heading reads
        // as a broken page to a customer. Admin canvas still shows the placeholder so
        // merchants can tell why nothing renders and go add related products.
        if (queryType === 'related' && !editorViewport) {
            return null;
        }
        return (
            <div className="w-full">
                {isCurrentCategory && <CatalogToolbar preview={Boolean(editorViewport)} />}
                <div className="flex flex-col items-center justify-center p-10 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                    <ImageIcon className="w-12 h-12 text-gray-300 mb-4" />
                    <p className="text-center text-gray-500 font-medium">{t('filters.no_results', 'Brak produktów spełniających kryteria.')}</p>
                </div>
            </div>
        );
    }

    // Pagination Logic
    const totalPages = isCurrentCategory
        ? Math.ceil(serverTotal / itemsPerPage)
        : Math.ceil(filteredProducts.length / itemsPerPage);

    // Every other queryType already fetches at most `limit` (or `limit`-derived) rows, so
    // this non-paginated slice is a no-op for them — but 'manual' fetches exactly the
    // merchant's full curated list regardless of `limit` (a pagination page-size setting
    // here, not a "how many total" cap), so slicing to it would silently drop selections.
    const visibleProducts = isCurrentCategory
        ? allProducts
        : (paginationEnabled
            ? filteredProducts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
            : queryType === 'manual'
                ? filteredProducts
                : filteredProducts.slice(0, itemsPerPage));

    const isEditor = !!editorViewport;
    const builderPreviewMode = isEditor && paginationEnabled && totalPages <= 1;
    const effectiveTotalPages = builderPreviewMode ? 3 : totalPages;

    const handlePageChange = (newPage: number) => {
        if (builderPreviewMode) return;
        setCurrentPage(newPage);
        if (catalogCtx) catalogCtx.setCurrentPage(newPage);
        if (typeof window !== 'undefined') {
            const url = new URL(window.location.href);
            if (newPage > 1) {
                url.searchParams.set('strona', String(newPage));
            } else {
                url.searchParams.delete('strona');
                url.searchParams.delete('page');
            }
            window.history.pushState({}, '', `${url.pathname}${url.search}`);
            gridContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const getPageUrl = (pageNum: number) => {
        if (typeof window === 'undefined') return pageNum > 1 ? `?strona=${pageNum}` : '?';
        const url = new URL(window.location.href);
        if (pageNum > 1) {
            url.searchParams.set('strona', String(pageNum));
        } else {
            url.searchParams.delete('strona');
            url.searchParams.delete('page');
        }
        return `${url.pathname}${url.search}`;
    };

    const renderPagination = () => {
        if (!paginationEnabled || (totalPages <= 1 && !builderPreviewMode)) return null;
        const pg = resolvePaginationAppearance(props as Record<string, any>);
        const pgScope = paginationScopeClass(props.blockId);
        const pgHoverCss = paginationHoverCss(pgScope, pg);

        return (
            <div className={cn(
                "mt-10 flex items-center gap-2",
                pgScope,
                paginationPosition === 'left' ? "justify-start" : 
                paginationPosition === 'right' ? "justify-end" : "justify-center"
            )} style={paginationContainerStyle(pg)}>
                {pgHoverCss && <style>{pgHoverCss}</style>}
                {paginationStyle === 'arrows' && (
                    <>
                        <a
                            href={currentPage > 1 ? getPageUrl(currentPage - 1) : '#'}
                            onClick={(e) => {
                                if (currentPage > 1) {
                                    e.preventDefault();
                                    handlePageChange(currentPage - 1);
                                }
                            }}
                            aria-label={t('pagination.prev', 'Poprzednia strona')}
                            data-pg-item
                            aria-disabled={currentPage === 1}
                            style={paginationArrowStyle(pg)}
                            className={cn(
                                "flex items-center justify-center w-10 h-10 rounded-full border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-all",
                                currentPage === 1 && "opacity-40 pointer-events-none cursor-not-allowed"
                            )}
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </a>
                        <span className="text-sm font-bold text-gray-500 px-4" style={{ color: pg.text, fontSize: pg.fontSize }}>
                            {t('pagination.page_x_of_y', 'Strona {current} z {total}').replace('{current}', String(currentPage)).replace('{total}', String(effectiveTotalPages))}
                        </span>
                        <a
                            href={currentPage < effectiveTotalPages ? getPageUrl(currentPage + 1) : '#'}
                            onClick={(e) => {
                                if (currentPage < effectiveTotalPages) {
                                    e.preventDefault();
                                    handlePageChange(currentPage + 1);
                                }
                            }}
                            aria-label={t('pagination.next', 'Następna strona')}
                            data-pg-item
                            aria-disabled={currentPage === effectiveTotalPages}
                            style={paginationArrowStyle(pg)}
                            className={cn(
                                "flex items-center justify-center w-10 h-10 rounded-full border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-all",
                                currentPage === effectiveTotalPages && "opacity-40 pointer-events-none cursor-not-allowed"
                            )}
                        >
                            <ChevronRight className="w-4 h-4" />
                        </a>
                    </>
                )}

                {paginationStyle === 'dots' && (
                    <div className="flex gap-2" style={paginationContainerStyle(pg)}>
                        {[...Array(effectiveTotalPages)].map((_, i) => (
                            <a
                                key={i}
                                href={getPageUrl(i + 1)}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handlePageChange(i + 1);
                                }}
                                aria-label={`${t('pagination.page', 'Strona')} ${i + 1}`}
                                aria-current={currentPage === i + 1 ? 'page' : undefined}
                                style={paginationDotStyle(pg, currentPage === i + 1)}
                                className={cn(
                                    "w-3 h-3 rounded-full transition-all duration-300 block",
                                    currentPage === i + 1 ? "bg-blue-600 scale-125" : "bg-gray-200 hover:bg-gray-300"
                                )}
                            />
                        ))}
                    </div>
                )}

                {paginationStyle === 'numbers' && (
                    <div className="flex items-center gap-1.5" style={paginationContainerStyle(pg)}>
                        <a
                            href={currentPage > 1 ? getPageUrl(currentPage - 1) : '#'}
                            onClick={(e) => {
                                if (currentPage > 1) {
                                    e.preventDefault();
                                    handlePageChange(currentPage - 1);
                                }
                            }}
                            aria-label={t('pagination.prev', 'Poprzednia strona')}
                            data-pg-item
                            aria-disabled={currentPage === 1}
                            style={paginationArrowStyle(pg, true)}
                            className={cn(
                                "flex items-center gap-1 h-9 px-2.5 rounded-lg border border-gray-200 text-gray-500 hover:text-gray-900 hover:bg-gray-50 transition-all text-sm font-medium",
                                currentPage === 1 && "opacity-40 pointer-events-none cursor-not-allowed"
                            )}
                        >
                            <ChevronLeft className="w-4 h-4" />
                            <span className="hidden sm:inline">{t('pagination.prev', 'Poprzednia')}</span>
                        </a>
                        {[...Array(effectiveTotalPages)].map((_, i) => {
                            if (effectiveTotalPages > 5) {
                                if (i !== 0 && i !== effectiveTotalPages - 1 && Math.abs(currentPage - 1 - i) > 1) {
                                    if (i === 1 || i === effectiveTotalPages - 2) return <span key={i} className="flex items-center px-0.5 text-gray-300"><MoreHorizontal className="w-4 h-4" /></span>;
                                    return null;
                                }
                            }
                            const pageNum = i + 1;
                            return (
                                <a
                                    key={i}
                                    href={getPageUrl(pageNum)}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handlePageChange(pageNum);
                                    }}
                                    aria-label={`${t('pagination.page', 'Strona')} ${pageNum}`}
                                    aria-current={currentPage === pageNum ? 'page' : undefined}
                                    data-pg-item
                                    data-pg-active={currentPage === pageNum ? 'true' : undefined}
                                    style={paginationItemStyle(pg, currentPage === pageNum)}
                                    className={cn(
                                        "flex items-center justify-center w-9 h-9 rounded-lg text-sm font-bold transition-all",
                                        currentPage === pageNum
                                            ? "bg-blue-600 text-white shadow-md"
                                            : "bg-white border border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                                    )}
                                >
                                    {pageNum}
                                </a>
                            );
                        })}
                        <a
                            href={currentPage < effectiveTotalPages ? getPageUrl(currentPage + 1) : '#'}
                            onClick={(e) => {
                                if (currentPage < effectiveTotalPages) {
                                    e.preventDefault();
                                    handlePageChange(currentPage + 1);
                                }
                            }}
                            aria-label={t('pagination.next', 'Następna strona')}
                            data-pg-item
                            aria-disabled={currentPage === effectiveTotalPages}
                            style={paginationArrowStyle(pg, true)}
                            className={cn(
                                "flex items-center gap-1 h-9 px-2.5 rounded-lg border border-gray-200 text-gray-500 hover:text-gray-900 hover:bg-gray-50 transition-all text-sm font-medium",
                                currentPage === effectiveTotalPages && "opacity-40 pointer-events-none cursor-not-allowed"
                            )}
                        >
                            <span className="hidden sm:inline">{t('pagination.next', 'Następna')}</span>
                            <ChevronRight className="w-4 h-4" />
                        </a>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div ref={gridContainerRef} className="w-full flex flex-col">
            {!isEditor && !props.isAdmin && (
                <ItemListTracker
                    products={visibleProducts}
                    listId={activeCategory?.id ? `category_${activeCategory.id}` : (categoryId ? `category_${categoryId}` : queryType)}
                    listName={activeCategory?.name || queryType}
                    currency={currency || 'PLN'}
                />
            )}
            {!props.isAdmin && showLoginGate && (
                <LoginGateModal isOpen={showLoginGate} onClose={() => setShowLoginGate(false)} />
            )}
            {!props.isAdmin && negotiateProduct && (
                <NegotiatePriceModal
                    isOpen={!!negotiateProduct}
                    onClose={() => setNegotiateProduct(null)}
                    productId={negotiateProduct.id}
                    catalogPrice={negotiateProduct.sales_price !== null && negotiateProduct.sales_price !== undefined ? negotiateProduct.sales_price : negotiateProduct.price}
                    currency={currency || 'PLN'}
                    quantity={1}
                />
            )}
            {isCurrentCategory && <CatalogToolbar preview={Boolean(editorViewport)} />}
            {isCarousel ? renderCarousel([
                ...visibleProducts.map((product, index) => (
                    <BuilderProductCard
                        key={product.id}
                        product={product}
                        index={index}
                        props={props}
                        siteConfig={siteConfig}
                        onOpenLoginGate={() => setShowLoginGate(true)}
                        onOpenNegotiate={(prod) => setNegotiateProduct(prod)}
                    />
                )),
                ...(props.viewAllUrl ? [
                    <Link
                        key="view-all"
                        href={props.viewAllUrl}
                        className="group/viewall flex h-full min-h-[220px] flex-col items-start justify-end gap-3 p-6 transition-colors"
                        style={{
                            backgroundColor: props.customStyles?.viewAllBg || 'transparent',
                            color: props.customStyles?.viewAllColor || props.customStyles?.titleColor || '#111827',
                            borderRadius: `${props.cardBorderRadius ?? 12}px`,
                            border: `1px solid ${props.cardBorderColor || '#f3f4f6'}`,
                        }}
                    >
                        <span className="text-xl font-bold leading-tight">{props.viewAllText || t('product.view_all', 'Zobacz wszystkie')}</span>
                        <ArrowRight className="w-6 h-6 transition-transform duration-300 group-hover/viewall:translate-x-1.5" />
                    </Link>,
                ] : []),
            ]) : (
                <div
                    className={cn("grid", !editorViewport && "storefront-product-grid", editorViewport && !gridGap && "gap-x-6 gap-y-8", gridColsClass)}
                    style={{ ...gridColsStyle, ...storefrontGridVars, ...(gridGap ? { gap: gridGap } : {}) }}
                >
                    {visibleProducts.map((product, index) => (
                        <BuilderProductCard
                            key={product.id}
                            product={product}
                            index={index}
                            props={props}
                            siteConfig={siteConfig}
                            onOpenLoginGate={() => setShowLoginGate(true)}
                            onOpenNegotiate={(prod) => setNegotiateProduct(prod)}
                        />
                    ))}
                </div>
            )}

            {!isCarousel && renderPagination()}
        </div>
    );
}
